import { checkEnv } from '@utils/shared';
import axiosInstance from '../config/axiosConfig';
import axios from 'axios';

let activeStreamController: AbortController | null = null;

export function abortActiveStream(): void {
    if (activeStreamController) {
        activeStreamController.abort();
        activeStreamController = null;
    }
}

export async function getAllSessions(email: string) {
    try {
        const response = await axiosInstance.get(`/usersession/getAllSessions/${email}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching sessions', error);
        throw error;
    }
}

export async function getSharedSessions(email: string) {
    try {
        const response = await axiosInstance.get(`/usersession/getSharedSessions/${email}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching sessions', error);
        throw error;
    }
}

export async function generateSessionTopic(
    email: string,
    sessionId: string,
    prompt: string,
    isMultiple: boolean,
    file?: File | null,
    dataFile?: File | null
) {
    try {
        const payload = new FormData();
        if (file) {
            payload.append('file', file);
        }
        if (dataFile) {
            payload.append('dataFile', dataFile);
        }
        payload.append('email', email);
        payload.append('sessionId', sessionId);
        payload.append('messageBody', prompt);
        payload.append('isMultiple', String(isMultiple));
        const response = await axiosInstance.post(`/chat/topic-file`, payload);
        return response.data;
    } catch (error) {
        console.error('Error generating topic', error);
        throw error;
    }
}

export async function createUserSession(email: string, designSystem: string, templateVersion: string = 'v2') {
    try {
        const response = await axiosInstance.post(`/usersession/create`, {
            email,
            designSystem,
            templateVersion,
        });
        return response.data;
    } catch (error) {
        console.error('Error creating session', error);
        throw error;
    }
}

export async function createChatSession(email: string, sessionId: string, templateVersion: string = 'v2') {
    try {
        const response = await axiosInstance.post(`/chatSession/create`, {
            email,
            sessionId,
            templateVersion,
        });
        return response.data;
    } catch (error) {
        console.error('Error creating chat session', error);
        throw error;
    }
}

export async function renameSessionTopic(email: string, sessionId: string, topic: string) {
    try {
        const response = await axiosInstance.post(`/usersession/renameTopic`, {
            email,
            sessionId,
            topic,
        });
        return response.data;
    } catch (error) {
        console.error('Error renaming session', error);
        throw error;
    }
}

export async function deleteSession(email: string, sessionId: string) {
    try {
        const response = await axiosInstance.delete(`/usersession/delete`, {
            withCredentials: true,
            data: { email, sessionId },
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting session', error);
        throw error;
    }
}

export async function removeShare(sharedEmail: string, sessionId: string) {
    try {
        const response = await axiosInstance.post(
            `/usersession/revokeShare?sessionId=${sessionId}&sharedEmail=${sharedEmail}`,
            null,
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error removing session share', error);
        throw error;
    }
}

export async function duplicateSession(sessionId: string, targetEmail?: string) {
    try {
        console.log('DEBUG - duplicateSession called with sessionId:', sessionId);
        console.log('DEBUG - duplicateSession called with targetEmail:', targetEmail);
        console.log('DEBUG - targetEmail type:', typeof targetEmail);
        console.log('DEBUG - targetEmail truthy?:', !!targetEmail);

        const url = `/usersession/copy?sourceSessionId=${sessionId}${targetEmail ? '&targetEmail=' + targetEmail : ''}`;
        console.log('DEBUG - Final URL:', url);

        const response = await axiosInstance.post(url, null, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error('Error duplicating session', error);
        throw error;
    }
}

export async function updateFavoriteSession(email: string, sessionId: string, favorite: boolean) {
    try {
        const response = await axiosInstance.post(`/usersession/favorite`, {
            email,
            sessionId,
            favorite,
        });
        return response.data;
    } catch (error) {
        console.error('Error updating favorite', error);
        throw error;
    }
}

export async function getMessagesBySessionId(email: string, sessionId: string) {
    try {
        const response = await axiosInstance.post(`/chatSessionMessage/getBySessionId`, { email, sessionId });
        return response.data;
    } catch (error) {
        console.error('Error fetching historical message by session id', error);
        throw error;
    }
}

export async function getProjectFiles(
    chatSessionId: number,
    commitId: string,
    designSystem: string,
    templateVersion: string = 'v2'
) {
    try {
        const response = await axiosInstance.get(`/chatSession/project`, {
            params: { chatSessionId, commitId, designSystem, templateVersion },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching project files', error);
        throw error;
    }
}

export async function getChatMessages(data: FormData) {
    try {
        const token = localStorage.getItem('idToken');

        abortActiveStream();
        activeStreamController = new AbortController();

        const response = await fetch(`/rapidcraft-service/api/rapidcraft/chat/streamMessage`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: data,
            signal: activeStreamController.signal,
        });

        if (!response.ok) {
            if (response.status === 401) {
                console.warn(`Authentication error (${response.status}): Clearing auth data and redirecting to login`);

                localStorage.removeItem('isUserLoggedIn');
                localStorage.removeItem('idToken');
                localStorage.removeItem('ads.loggedInUser');
                sessionStorage.removeItem('ads.loggedInUser');

                if (!window.location.pathname.includes('/login')) {
                    window.location.href = '/login';
                }

                throw new Error('Session expired. Please log in again.');
            }

            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            console.info('[getSessionApis] Stream fetch aborted by user');
            return null;
        }
        console.error('Error fetching chat message', error);
        throw error;
    }
}

export async function getDownloadUrl(chatSessionId: number) {
    try {
        const response = await axiosInstance.post(`/chat/download`, {
            chatSessionId,
        });
        return response.data;
    } catch (error) {
        console.error('Error get project download url', error);
        throw error;
    }
}

export async function updateUserSession(
    sessionId: string,
    email: string,
    thumbnail: string,
    thmemConfig: Record<string, string>
) {
    try {
        const response = await axiosInstance.post(`/usersession/update`, {
            email,
            sessionId,
            thumbnail,
            thmemConfig,
        });
        return response.data;
    } catch (error) {
        console.error('Error get project download url', error);
        throw error;
    }
}

export async function generateThumbnail(prompt: string) {
    const env = checkEnv();
    const domainMap: Record<string, string> = {
        qa: 'https://designdraft.qa.pwc.com/api',
        local: 'http://localhost:8083/api',
        dev: 'https://designdraft.dev.pwc.com/api',
        prod: 'https://designdraft.pwc.com/api',
    };

    try {
        const response = await axios.post(`${domainMap[env]}/copilot/rapidcraft/generate_image`, {
            prompt,
        });
        return response.data;
    } catch (error) {
        console.error('Error generating thumbnail', error);
        throw error;
    }
}

export async function terminateGeneration(generationId: string) {
    const env = checkEnv();
    const domainMap: Record<string, string> = {
        qa: 'https://designdraft.qa.pwc.com/api',
        local: 'http://localhost:8083/api',
        dev: 'https://designdraft.dev.pwc.com/api',
        prod: 'https://designdraft.pwc.com/api',
    };

    try {
        const response = await axios.post(`${domainMap[env]}/copilot/rapidcraft/terminate_generation`, {
            generationId,
        });
        return response.data;
    } catch (error) {
        console.error('Error generating thumbnail', error);
        throw error;
    }
}

export async function getGenerationStatus(generationId: string): Promise<{ status: string; error?: string } | null> {
    const env = checkEnv();
    const domainMap: Record<string, string> = {
        qa: 'https://designdraft.qa.pwc.com/api',
        local: 'http://localhost:8083/api',
        dev: 'https://designdraft.dev.pwc.com/api',
        prod: 'https://designdraft.pwc.com/api',
    };

    try {
        const response = await axios.get(`${domainMap[env]}/copilot/rapidcraft/generation_status/${generationId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching generation status', error);
        return null;
    }
}

/**
 * Process an image file via backend pipeline (resize/standardize to max 2000px PNG)
 */
export async function processImage(file: File): Promise<Blob> {
    const payload = new FormData();
    payload.append('file', file);

    const token = localStorage.getItem('idToken');
    const response = await fetch(`/rapidcraft-service/api/rapidcraft/attachment/processImage`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: payload,
    });

    if (!response.ok) {
        throw new Error(`Image processing failed: HTTP ${response.status}`);
    }

    return response.blob();
}

/**
 * Process a data file (CSV/XLSX/XLS/JSON) via backend pipeline, returns TOON string
 */
export async function processData(file: File, maxRows?: number): Promise<string> {
    const payload = new FormData();
    payload.append('file', file);
    if (maxRows !== undefined) {
        payload.append('maxRows', String(maxRows));
    }

    const token = localStorage.getItem('idToken');
    const response = await fetch(`/rapidcraft-service/api/rapidcraft/attachment/processData`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: payload,
    });

    if (!response.ok) {
        throw new Error(`Data processing failed: HTTP ${response.status}`);
    }

    const result = await response.json();
    if (!result.toon) {
        throw new Error('Data processing returned empty TOON');
    }

    return result.toon;
}

/**
 * Get existing public share link for a session
 */
export async function getPublicShareLink(sessionId: string) {
    try {
        const response = await axiosInstance.get(`/public/getSharedLink?sessionId=${sessionId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching public share link', error);
        throw error;
    }
}

/**
 * Generate or update a public share link for a session
 */
export async function generatePublicShareLink(sessionId: string, isPublic: boolean = true) {
    try {
        const response = await axiosInstance.post(
            `/public/generateSharedLink?sessionId=${sessionId}&isPublic=${isPublic}`,
            null,
            { withCredentials: true }
        );
        return response.data;
    } catch (error) {
        console.error('Error generating/updating public share link', error);
        throw error;
    }
}
